import type { CSSProperties } from 'react';
import { TrendUp, TrendDown, Minus } from '@phosphor-icons/react';

// DeltaChip renders a signed change with a direction arrow. By default up is the
// good direction (eNPS, driver means, member growth are higher-is-better). Pass
// `invert` when a rising number is bad (e.g. overdue amount, drop-out rate) so
// the colour tracks sentiment rather than raw direction.
//
// Shared primitive (lifted from Pulse.tsx). Every chip is the same pill — same
// padding, always an arrow-sized icon slot (up / down / neutral dash) — so a
// zero delta lines up with its neighbours instead of collapsing.
export function DeltaChip({
  delta,
  digits = 0,
  invert = false,
  suffix = '',
}: {
  delta: number;
  digits?: number;
  invert?: boolean;
  suffix?: string;
}) {
  const up = delta > 0;
  const down = delta < 0;
  const good = invert ? down : up;
  const bad = invert ? up : down;
  const style: CSSProperties = good
    ? { backgroundColor: 'color-mix(in srgb, var(--lime) 32%, transparent)', color: 'var(--ink)' }
    : bad
      ? { backgroundColor: 'color-mix(in srgb, var(--color-error) 15%, transparent)', color: 'var(--color-error)' }
      : {
          backgroundColor: 'color-mix(in srgb, var(--color-fg) 12%, transparent)',
          color: 'var(--color-muted)',
        };
  return (
    <span
      className="inline-flex items-center gap-0.5 rounded-pill px-2 py-0.5 text-xs font-semibold tabular-nums"
      style={style}
    >
      {up ? (
        <TrendUp size={12} weight="bold" />
      ) : down ? (
        <TrendDown size={12} weight="bold" />
      ) : (
        <Minus size={12} weight="bold" />
      )}
      {delta > 0 ? '+' : ''}
      {delta.toFixed(digits)}
      {suffix}
    </span>
  );
}
